import React, { Dispatch, ReactNode, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import styled from 'styled-components';

import ModalContainer from 'app/components/ModalContainer';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { Title } from 'app/pages/Agent/Profile';
import { formatNumber } from 'utils/helper';
import StyledButton from 'app/components/StyledButton';
import { ApprovalOrderFilter } from '.';
import useApproveOrder from './useApproveOrder';
import { NotiType } from 'app/components/Modals/DialogNotification';

interface FastApprovalModalProps {
  data: Record<string, any> | null;
  onCloseModal: () => void;
  onFilterApprovalOrder: (params?: Partial<ApprovalOrderFilter>) => void;
  setWarningMsg: Dispatch<SetStateAction<ReactNode>>;
  setWarningType?: Dispatch<SetStateAction<NotiType>>;
}

const FastApprovalModal: React.FC<FastApprovalModalProps> = props => {
  const {
    data,
    onCloseModal,
    onFilterApprovalOrder,
    setWarningMsg,
    setWarningType,
  } = props;

  const translations = useSelector(selectTranslations);

  const { handleAcceptOrder, handleRejectOrder, approvalType } =
    useApproveOrder({
      acceptOrder: {
        onSuccess: () => {
          setWarningMsg(translations?.['b2b.txt.approvalSucc'] ?? '');
          setWarningType && setWarningType('SUCCESS');
        },
        onError: error => {
          setWarningMsg(error?.message);
          setWarningType && setWarningType('ERROR');
        },
        onFinally: () => {
          onCloseModal();
          onFilterApprovalOrder();
        },
      },
      rejectOrder: {
        onSuccess: () => {
          setWarningMsg(translations?.['b2b.txt.refuseSucc'] ?? '');
          setWarningType && setWarningType('SUCCESS');
        },
        onError: error => {
          setWarningMsg(error?.message);
          setWarningType && setWarningType('ERROR');
        },
        onFinally: () => {
          onCloseModal();
          onFilterApprovalOrder();
        },
      },
    });

  return (
    <ModalContainer
      isShow={!!data}
      onCloseDialog={onCloseModal}
      styleSlots={{
        modalContentStyle: {
          width: '90vw',
          maxWidth: '459px',
          height: 'auto',
        },
        iconWrapperStyle: {
          margin: 0,
        },
      }}
      maskClosable={false}
      modalProps={{
        destroyOnClose: true,
      }}
    >
      <Title style={{ fontSize: 16, textTransform: 'initial' }}>
        {translations ? translations?.['b2b.txt.approvalOrderNumber'] : ''}{' '}
        <span style={{ color: '#FF7B01' }}>{data?.id}</span>
      </Title>
      <Row gutter={[10, 10]} style={{ marginTop: 20 }}>
        <Col span={24}>
          <Row>
            <Col span={11}>
              <SubTitle>{translations?.['b2c.txt.orderNumber'] ?? ''}</SubTitle>
            </Col>
            <Col span={13}>
              <SubData>{data?.id}</SubData>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row>
            <Col span={11}>
              <SubTitle>{translations?.['b2b.txt.orderer'] ?? ''}</SubTitle>
            </Col>
            <Col span={12}>
              <SubData>{data?.agent?.name}</SubData>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row>
            <Col span={11}>
              <SubTitle>{translations?.['b2b.txt.orderDate'] ?? ''}</SubTitle>
            </Col>
            <Col span={12}>
              <SubData>{data?.orderDate?.dateDMY}</SubData>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row>
            <Col span={11}>
              <SubTitle>
                {translations?.['b2b.txt.ticketAmount'] ?? ''}
              </SubTitle>
            </Col>
            <Col span={12}>
              <SubData>{data?.totalTicket}</SubData>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row>
            <Col span={11}>
              <SubTitle>{translations?.['b2b.txt.method'] ?? ''}</SubTitle>
            </Col>
            <Col span={12}>
              <SubData>{data?.payment?.name}</SubData>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row>
            <Col span={11}>
              <SubTitle>
                {translations?.['b2b.txt.total-amount'] ?? ''}
              </SubTitle>
            </Col>
            <Col span={12}>
              <SubData style={{ color: '#FF7B01', fontSize: 16 }}>
                {formatNumber(data?.totalPrice).replaceAll(',', '.')}
                {'đ'}
              </SubData>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={14} style={{ marginTop: 24 }}>
        <Col span={12}>
          <StyledButton
            textColor="#808080"
            border="solid 1px #808080"
            bgColor="#fff"
            onClick={handleRejectOrder.bind(null, data?.idHash)}
            style={{ fontSize: 16 }}
            width="100%"
          >
            {approvalType === 'REJECT'
              ? 'Loading...'
              : translations?.['b2b.approvalStt.refuse']}
          </StyledButton>
        </Col>
        <Col span={12}>
          <StyledButton
            textColor="#fff"
            onClick={handleAcceptOrder.bind(null, data?.idHash)}
            style={{ fontSize: 16 }}
            width="100%"
          >
            {approvalType === 'ACCEPT'
              ? 'Loading...'
              : translations?.['b2b.btn.accept']}
          </StyledButton>
        </Col>
      </Row>
    </ModalContainer>
  );
};

export default FastApprovalModal;

const SubTitle = styled.p`
  color: #808080;
  font-size: 14px;
  margin: 0;
`;

const SubData = styled.p`
  color: #000000;
  font-size: 14px;
  font-weight: 600;
  margin: 0;
`;
